/**
 * MAIN HUB HOME PAGE
 * Special home page for drdnreddyortho.com - the central hub
 * Showcases all specialties with unique premium design
 * Displays full credentials prominently
 */

import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useSite } from '../contexts/SiteContext';
import { PatientJourneyTimeline } from '../components/PatientJourneyTimeline';
import { 
  ArrowRight, 
  Award, 
  Clock, 
  Users, 
  Star,
  ExternalLink,
  CheckCircle2,
  Play,
  Phone,
  Calendar,
  MapPin,
  GraduationCap,
  Building2,
  BadgeCheck
} from 'lucide-react';

export const MainHubHome = () => {
  const { 
    heroTitle, 
    heroSubtitle, 
    heroDescription, 
    theme,
    doctor,
    specialties,
    symptoms
  } = useSite();

  const stats = [
    { number: '30+', label: 'Years Experience', icon: Clock },
    { number: '15000+', label: 'Patients Treated', icon: Users },
    { number: '5000+', label: 'Surgeries Performed', icon: Award },
    { number: '5.0', label: 'Google Rating', icon: Star }
  ];

  return (
    <div className="min-h-screen" data-testid="main-hub-home">
      {/* Hero Section - Premium Design */}
      <section className="relative min-h-[90vh] flex items-center bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_left,_rgba(59,130,246,0.3)_0%,_transparent_50%)]" />
          <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_bottom_right,_rgba(249,115,22,0.2)_0%,_transparent_50%)]" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-sm mb-6">
                <span className="text-orange-400 font-semibold">ORTHOPAEDIC & SPORTS INJURY SURGEON</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
                {doctor.name}
              </h1>
              
              <p className="text-xl md:text-2xl text-blue-300 mb-4 font-medium">
                {doctor.title}
              </p>
              
              <p className="text-lg text-gray-300 mb-6 italic">
                {doctor.tagline}
              </p>

              {/* Credentials - Prominently Displayed */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-8 border border-white/20">
                <div className="flex items-center gap-2 mb-3">
                  <GraduationCap className="w-5 h-5 text-orange-400" />
                  <span className="text-orange-400 font-semibold text-sm uppercase tracking-wider">Qualifications</span>
                </div>
                <p className="text-white text-lg font-medium mb-4">
                  {doctor.credentials}
                </p>
                <div className="flex items-center gap-2 mb-2">
                  <Award className="w-5 h-5 text-blue-400" />
                  <span className="text-blue-400 font-semibold text-sm uppercase tracking-wider">Fellowships</span>
                </div>
                <p className="text-gray-300">
                  {doctor.fellowships}
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a href={`tel:${doctor.phone}`}>
                  <Button 
                    size="lg" 
                    className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white w-full sm:w-auto"
                    data-testid="book-appointment-btn"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Call: {doctor.phone}
                  </Button>
                </a>
                <a href={doctor.consultationLink} target="_blank" rel="noopener noreferrer">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-2 border-white/30 text-white hover:bg-white/10 w-full sm:w-auto"
                    data-testid="call-now-btn"
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    Book Appointment
                  </Button>
                </a>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
                {stats.map((stat, index) => {
                  const Icon = stat.icon;
                  return (
                    <div key={index} className="text-center bg-white/5 rounded-lg p-3">
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <Icon className="w-4 h-4 text-orange-400" />
                        <span className="text-2xl font-bold text-white">{stat.number}</span>
                      </div>
                      <p className="text-xs text-gray-400">{stat.label}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Doctor Profile Card */}
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
                <div className="aspect-[4/5] rounded-xl overflow-hidden mb-6 shadow-2xl">
                  <img
                    src={doctor.doctorImage}
                    alt={doctor.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                {/* Hospital Info */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-orange-500/20 rounded-full flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">{doctor.hospitalFull}</p>
                    <p className="text-gray-400 text-sm">{doctor.addressShort}</p>
                  </div>
                </div>
                
                {/* Timings */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                    <Clock className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">Consultation Hours</p>
                    <p className="text-gray-400 text-sm">{doctor.timings}</p>
                  </div>
                </div>
              </div>
              
              {/* Floating badge */}
              <div className="absolute -top-4 -right-4 bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg flex items-center gap-2">
                <Star className="w-4 h-4 fill-white" />
                {doctor.rating} Google Rating
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Experience & Positions Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Experience & Positions
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A distinguished career spanning three decades across India and the United Kingdom
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {doctor.positions?.map((position, index) => (
              <Card key={index} className="relative overflow-hidden hover:shadow-xl transition-shadow">
                <div 
                  className="absolute top-0 left-0 w-full h-1"
                  style={{ backgroundColor: index === 0 ? '#f97316' : index === 1 ? '#3b82f6' : '#22c55e' }}
                />
                <CardHeader>
                  <div 
                    className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
                    style={{ backgroundColor: index === 0 ? '#fff7ed' : index === 1 ? '#eff6ff' : '#f0fdf4' }}
                  >
                    <Building2 
                      className="w-7 h-7" 
                      style={{ color: index === 0 ? '#f97316' : index === 1 ? '#3b82f6' : '#22c55e' }}
                    />
                  </div>
                  <CardTitle className="text-xl">{position.title}</CardTitle>
                  <CardDescription className="text-base">
                    <span className="font-semibold text-gray-900">{position.organization}</span>
                    <br />
                    <span className="text-gray-500">{position.location}</span>
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Professional Memberships */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Professional Memberships
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Active member of prestigious national and international orthopaedic organizations
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {doctor.memberships?.map((membership, index) => (
              <div 
                key={index}
                className="flex items-center gap-4 bg-white p-5 rounded-xl shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
                  <BadgeCheck className="w-6 h-6 text-blue-600" />
                </div>
                <p className="text-gray-800 font-medium">{membership}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialties Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Areas of Expertise
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Specialized care for every part of your upper limb
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {specialties?.map((specialty) => (
              <a
                key={specialty.id}
                href={specialty.link}
                target="_blank"
                rel="noopener noreferrer"
                className="block group"
              >
                <Card 
                  className="h-full overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
                  style={{ borderTop: `4px solid ${specialty.theme.primaryHex}` }}
                >
                  <div 
                    className="h-48 flex items-center justify-center"
                    style={{ backgroundColor: `${specialty.theme.primaryHex}10` }}
                  >
                    <span className="text-8xl group-hover:scale-110 transition-transform">
                      {specialty.icon}
                    </span>
                  </div>
                  <CardHeader>
                    <CardTitle className="text-2xl flex items-center gap-2">
                      {specialty.title}
                      <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-current transition-colors" />
                    </CardTitle>
                    <CardDescription className="text-gray-600 text-base">
                      {specialty.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button 
                      style={{ backgroundColor: specialty.theme.primaryHex }}
                      className="w-full text-white hover:opacity-90"
                    >
                      Explore {specialty.title} Care
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Personal Journey Quote */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-6xl mb-8 opacity-30">"</div>
            <blockquote className="text-2xl md:text-3xl font-light italic mb-8 leading-relaxed">
              I love being able to help people regain their mobility and get back to the things they love.
            </blockquote>
            <p className="text-xl text-blue-200">— {doctor.name}</p>
          </div>
        </div>
      </section>

      {/* When to Visit Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                When Should You Visit?
              </h2>
              <p className="text-lg text-gray-600">
                Don't ignore these warning signs - early treatment leads to better outcomes
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {symptoms.map((symptom, index) => (
                <div 
                  key={index} 
                  className="flex items-start gap-3 p-4 rounded-lg bg-blue-50 border-l-4 border-blue-500"
                >
                  <CheckCircle2 className="w-6 h-6 text-blue-500 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700">{symptom}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Patient Journey Timeline */}
      <PatientJourneyTimeline />

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Book Your Consultation Today
          </h2>
          <p className="text-xl text-gray-300 mb-4 max-w-3xl mx-auto">
            Get expert guidance from {doctor.name} — fellowship-trained orthopaedic and sports injury specialist. 
            Comprehensive evaluation and treatment for shoulder, elbow, wrist, hip, knee, and ankle conditions.
          </p>
          <p className="text-lg text-gray-400 mb-8">
            📍 {doctor.hospitalFull} | ⏰ {doctor.timings}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={doctor.consultationLink} target="_blank" rel="noopener noreferrer">
              <Button 
                size="lg" 
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Appointment
              </Button>
            </a>
            <a href={`tel:${doctor.phone}`}>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white/10"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call: {doctor.phone}
              </Button>
            </a>
            <a href={doctor.whatsappLink} target="_blank" rel="noopener noreferrer">
              <Button 
                size="lg" 
                className="bg-green-500 hover:bg-green-600 text-white"
              >
                💬 WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Video Section */}
      {doctor.socialMedia?.youtube && (
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Patient Success Stories
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Watch real-life recovery stories from patients treated by {doctor.name}
            </p>
            <a 
              href={doctor.socialMedia.youtube}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-red-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-red-700 transition-colors"
            >
              <Play className="w-6 h-6" />
              Watch Videos on YouTube
              <ExternalLink className="w-5 h-5" />
            </a>
          </div>
        </section>
      )}
    </div>
  );
};
